// src/components/fix-actions/fix-actions-header.tsx
import { CheckCircle2, Sparkles } from "lucide-react";
import { ModuleBreadcrumb } from "../shared/module-breadcrumb";
import { formatPriorityUpdatedAt } from "./fix-actions-utils";

interface FixActionsHeaderProps {
  onNavigate?: (page: string) => void;
  highPriorityCount: number;
  quickWinsCount: number;
  completedCount: number;
  priorityUpdatedAt?: string | null;
}

export function FixActionsHeader({
  onNavigate,
  highPriorityCount,
  quickWinsCount,
  completedCount,
  priorityUpdatedAt,
}: FixActionsHeaderProps) {
  return (
    <div
      className="px-4 md:px-8 py-6 md:py-8 border-b"
      style={{ borderColor: "var(--border)", backgroundColor: "var(--background)" }}
    >
      {/* Breadcrumb */}
      <ModuleBreadcrumb currentModuleId="fix-actions" onNavigate={onNavigate} />

      {/* Hero Title — scales down on mobile */}
      <div className="mb-3 md:mb-4 mt-4">
        <h2
          style={{
            fontSize: "clamp(var(--text-xl), 4.5vw, var(--text-2xl))",
            fontWeight: "var(--font-weight-semibold)",
            color: "var(--foreground)",
            lineHeight: "1.3",
          }}
        >
          Prioritize optimization actions. Automate assets.
        </h2>
      </div>

      {/* Description */}
      <p
        style={{
          color: "var(--muted-foreground)",
          fontSize: "var(--text-base)",
          marginBottom: "20px",
          maxWidth: "800px",
        }}
      >
        AI-prioritized optimization roadmap based on your perception baseline. Start with
        high-impact actions, deploy with confidence, then measure improvement.
      </p>

      {/* Metadata Row — wraps onto multiple lines on narrow screens */}
      <div className="space-y-3">
        {/* Action Stats: label stacks above content on <sm, shares row on sm+ */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
          <span
            className="shrink-0"
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--muted-foreground)",
              fontWeight: "var(--font-weight-medium)",
            }}
          >
            Action Status:
          </span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0">
            <div className="flex items-center gap-2 shrink-0">
              <div
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: "var(--infrared-coral)" }}
              />
              <span
                style={{
                  fontSize: "var(--text-sm)",
                  color: "var(--foreground)",
                  fontWeight: "var(--font-weight-medium)",
                }}
              >
                {highPriorityCount}
              </span>
              <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                high priority
              </span>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <Sparkles className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--alpha-teal)" }} />
              <span
                style={{
                  fontSize: "var(--text-sm)",
                  color: "var(--foreground)",
                  fontWeight: "var(--font-weight-medium)",
                }}
              >
                {quickWinsCount}
              </span>
              <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                quick wins
              </span>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <CheckCircle2
                className="w-3.5 h-3.5 shrink-0"
                style={{ color: "var(--chart-3)" }}
              />
              <span
                style={{
                  fontSize: "var(--text-sm)",
                  color: "var(--foreground)",
                  fontWeight: "var(--font-weight-medium)",
                }}
              >
                {completedCount}
              </span>
              <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                completed
              </span>
            </div>
          </div>
        </div>

        {/* Last Updated */}
        <div className="flex items-center gap-2 flex-wrap">
          <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
            Priority Updated: {formatPriorityUpdatedAt(priorityUpdatedAt)}
          </span>
        </div>
      </div>
    </div>
  );
}